import { Box, FormLabel, Snackbar, Typography } from "@mui/material";
import styled, { css } from "styled-components/macro";
import { DeviceType } from "../../utils/hooks/useDeviceHook";
import { SpinnerWrapper } from "../shared/Spinner/SpinnerLoading.styles";
import { Button } from "../shared/Button/Button";
import { RedrawHeaderButton } from "../Redraw/RedrawTable.styles";
import { testOrderSecondaryCss } from "../shared/TestOrderDisplay.styles";
import { StatusIconSlot } from "../shared/StatusIconSlot.styles";
import {
  CheckboxLabelWrapper,
  FetalAntigenIndentUploadGrid,
  NewReqFetalAntigenGrid,
  CollapsiblePlateletGrid,
  OptionLabel,
} from "../NewRequisition/NewRequisition.styles";
import { NewReqFileUploaderDropzoneAntigen } from "../NewRequisition/NewReqFileUploader.styles";

const providerHeaderToolbarButtonCss = css`
  && {
    height: 45px;
    min-width: 125px;
    font-size: 21px;
    font-family: bold;
    line-height: 20px;
    letter-spacing: 0.02em;
    white-space: nowrap;
    @media only screen and (max-width: 450px) {
      width: max-content;
      min-width: 0px;
      padding: 4px 8px;
    }
  }
`;

export const ProviderHeaderActionButton = styled(Button)`
  ${providerHeaderToolbarButtonCss}
`;

export const ProviderHeaderRedrawButton = styled(RedrawHeaderButton)`
  ${providerHeaderToolbarButtonCss}
`;

export const CustomGridWrapper = styled.div`
  margin: 32px 20px;

  @media (max-width: 600px) {
    margin: 10px;
  }

  .ag-cell {
    font-family: light;
    display: flex;
    align-items: center;
    border: none;

    div {
      width: 100%;
      user-select: text;
    }
  }
  .ag-header-container {
    background-color: ${({ theme }) => theme.white};
    font-family: sans-serif !important;
    font-weight: bolder !important;
  }

  .ag-center-cols-viewport,
  .ag-body-horizontal-scroll-viewport {
    background-color: ${({ theme }) => theme.white};
    overscroll-behavior-x: none;
  }

  .ag-header-viewport {
    background-color: ${({ theme }) => theme.white};
  }

  /* Allow absolutely-positioned dropdowns (e.g. DownloadDropdown) to overflow
     the cell and row boundaries. Only applied on hover so normal row rendering
     is unaffected. */
  .ag-row-hover {
    .ag-cell {
      overflow: visible;
    }
  }

  .ag-row-hover.row-processing-completed {
    box-shadow: 0px 2px 4px -2px rgba(0, 0, 0, 0.2);
    z-index: 10;

    .grid-name-cell {
      text-decoration: underline !important;
    }
  }

  .ag-row-hover.row-electronic-order {
    cursor: pointer;
  }

  /* Ordered rows: match More-menu item hover (greenSubtle) instead of AG Grid blue. */
  .row-electronic-order {
    --ag-row-hover-color: ${({ theme }) => theme.greenSubtle};
  }

  /* Keep Ordered-row hover paint while the portaled More menu is open;
     AG Grid removes .ag-row-hover as soon as the pointer leaves the row. */
  .ag-row.row-ordered-actions-menu-open::before {
    content: "";
    background-color: var(--ag-row-hover-color);
    display: block;
    position: absolute;
    inset: 0;
    pointer-events: none;
  }

  .ag-cell-focus,
  .ag-cell-no-focus {
    border: none !important;
  }

  .ag-theme-alpine .ag-cell:focus {
    border: none !important;
    outline: none !important;
  }

  .no-border.ag-cell:focus {
    border: none;
    outline: none;
  }

  .row-not-read {
    background-color: ${({ theme }) => theme.rowNotReadGreen} !important;

    .grid-name-cell {
      font-family: "light";
      font-weight: 600 !important;
    }
  }

  .row-missing-info {
    background-color: ${({ theme }) => theme.rowMissingInfoRed} !important;
    .ag-cell {
      font-family: "bold" !important;
      font-weight: bolder !important;
    }
  }

  /* Paired with the injected CFC banner above it: together they render as a
     single card with a dark-blue frame. Banner drops its bottom divider so
     the two rows read as one. AG Grid splits rows across pinned-left and
     center DOM containers, so each container draws only its outer edge of
     the frame. On mobile (no pinned columns) the whole frame lives on the
     center row. */
  .ag-row.cfc-banner-row {
    border-bottom: none;
  }

  .ag-row.row-cfc-frame {
    border-bottom: 2px solid ${({ theme }) => theme.blueDark};
  }

  .cfc-has-pinned-cols {
    .ag-pinned-left-cols-container .ag-row.row-cfc-frame {
      border-left: 2px solid ${({ theme }) => theme.blueDark};
      border-bottom-left-radius: 8px;
    }

    .ag-center-cols-container .ag-row.row-cfc-frame {
      border-right: 2px solid ${({ theme }) => theme.blueDark};
      border-bottom-right-radius: 8px;
    }
  }

  .cfc-no-pinned-cols {
    .ag-center-cols-container .ag-row.row-cfc-frame {
      border-left: 2px solid ${({ theme }) => theme.blueDark};
      border-right: 2px solid ${({ theme }) => theme.blueDark};
      border-radius: 0 0 8px 8px;
    }
  }

  .cell-missing-info {
    font-family: "bold";
  }

  .grid-result-cell {
    font-family: "light";
    font-weight: 600;
  }

  .ag-pinned-left-header,
  .ag-pinned-left-cols-container {
    z-index: 11;

    &::after {
      content: "";
      position: absolute;
      top: 0;
      right: -6px;
      width: 6px;
      height: 100%;
      background: linear-gradient(
        to right,
        rgba(0, 0, 0, 0.2) 0%,
        rgba(0, 0, 0, 0.12) 25%,
        rgba(0, 0, 0, 0.05) 55%,
        rgba(0, 0, 0, 0.01) 80%,
        transparent 100%
      );
      pointer-events: none;
    }
  }

  .ag-pinned-left-header {
    background-color: ${({ theme }) => theme.white};
    border-right: none;
    overflow: visible;
  }

  .ag-header-cell-label {
    justify-content: start;
  }

  .ag-header-cell:last-child .ag-header-cell-resize::after {
    background-color: white !important;
  }

  .ag-header-cell-text {
    text-transform: uppercase;
    color: ${({ theme }) => theme.greyMedium};
    font-size: 14px;
    text-align: left;
  }

  .rhd-header .ag-header-cell-text {
    text-transform: none;
  }

  .ag-row {
    background-color: white;
    border-color: ${({ theme }) => theme.neutral100};
    user-select: none !important;
  }

  .ag-header,
  .ag-root-wrapper {
    border-color: ${({ theme }) => theme.neutral100};
    border-top: none;
  }
  .ag-theme-alpine
    .ag-cell.ag-cell-last-left-pinned:not(.ag-cell-range-right):not(
      .ag-cell-range-single-cell
    ) {
    border-right: none;
  }
`;

export const TabPanelContent = styled(Box)`
  padding: 32px 0 0 0;

  @media (max-width: 600px) {
    padding-top: 10px;
  }
`;

export const ReportDataCellWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  width: 100%;
  margin: 2px 0;
`;

export const GridReportIconSlot = styled(StatusIconSlot)<{
  $isMuted?: boolean;
}>`
  width: 24px;
  color: ${({ theme }) => theme.neutral500};
  filter: ${({ $isMuted }) => ($isMuted ? "grayscale(1)" : "none")};
`;

export const ReportStatusContent = styled.span<{
  $hasSecondaryLabel?: boolean;
}>`
  display: flex;
  align-items: ${({ $hasSecondaryLabel }) =>
    $hasSecondaryLabel ? "flex-start" : "center"};
  gap: 4px;
`;

export const ReportStatusStack = styled.span`
  display: inline-flex;
  flex-direction: column;
  white-space: nowrap;
  color: ${({ theme }) => theme.greyMedium};
`;

export const ReportStatusPrimary = styled.span`
  font-family: inherit;
  font-size: 14px;
  line-height: 20px;
`;

export const ReportStatusSecondary = styled.span<{ $isMuted?: boolean }>`
  ${testOrderSecondaryCss}
  color: ${({ theme, $isMuted }) =>
    $isMuted ? theme.neutral700 : theme.greyMedium};
`;

export const DownloadWrapper = styled.span<{ isDesktop: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 24px;
  cursor: pointer;
  margin-left: auto;
  z-index: 5;
  :hover {
    .download-rect {
      fill: ${({ isDesktop }) =>
        isDesktop ? "#bbd8d1" : undefined} !important;
    }
  }
`;

const getNameColorDevice = (deviceType: DeviceType) =>
  deviceType !== "desktop" ? "dodgerblue" : undefined;

export const NameWrapper = styled.div<{
  isMissing: boolean;
  deviceType: DeviceType;
}>`
  text-transform: uppercase;
  text-decoration: ${({ deviceType }) =>
    deviceType !== "desktop" ? "underline" : undefined};
  color: ${({ theme, isMissing, deviceType }) =>
    isMissing ? theme.redDark : getNameColorDevice(deviceType)};
  font-weight: ${({ isMissing }) => (isMissing ? 900 : undefined)};
  align-items: center;
  display: flex;
  cursor: pointer;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  display: block;

  :hover {
    opacity: 0.8;
  }

  svg {
    margin-left: 5px;
    margin-bottom: -6px;
    min-width: 22px;
  }
`;

export const AGGridWrapper = styled.div`
  height: 100%;
  width: 100%;
  user-select: none;
`;

export const GridWithPagination = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 80vh;
  box-shadow: -1px 4px 4px -2px rgba(0, 0, 0, 0.25);
`;

export const GridBody = styled.div`
  flex: 1;
  min-height: 0;
`;

export const GridPaginationPanel = styled.div`
  display: grid;
  grid-template-columns: max-content 1fr max-content;
  align-items: center;
  column-gap: 16px;
  min-height: 72px;
  padding: 0 16px;
  color: ${({ theme }) => theme.greyDark};
  font-family: light;
  font-size: 14px;
  background-color: ${({ theme }) => theme.neutral50};
  border: 1px solid ${({ theme }) => theme.neutral100};
  border-top: none;
`;

export const GridPageSizeControl = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const GridPaginationSummary = styled.div`
  justify-self: center;
  font-weight: normal;
  color: ${({ theme }) => theme.black};
  white-space: nowrap;
`;

export const GridPaginationControls = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const GridPaginationButton = styled.button`
  padding: 8px 18px;
  cursor: pointer;
  border: none;
  border-radius: 50px;
  font-weight: bold;
  font-size: 14px;
  color: ${({ theme }) => theme.greyDark};
  background-color: ${({ theme }) => theme.neutral100};

  &:disabled {
    cursor: not-allowed;
    opacity: 0.45;
  }

  @media (max-width: 600px) {
    padding: 4px 8px;
  }
`;

export const GridPaginationIconButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  padding: 0;
  cursor: pointer;
  border: none;
  color: ${({ theme }) => theme.greyDark};
  background: transparent;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.45;
  }

  @media (max-width: 600px) {
    display: none;
  }
`;

export const GridPaginationPageLabel = styled.span`
  color: ${({ theme }) => theme.black};
  white-space: nowrap;
`;

export const GridPageSizeSelect = styled.select`
  height: 32px;
  border: 1px solid ${({ theme }) => theme.neutral100};
  border-radius: 4px;
  color: ${({ theme }) => theme.greyDark};
  background-color: ${({ theme }) => theme.white};
`;

export const ReportsCellWrapper = styled.span`
  align-items: stretch;
  justify-content: start;
  width: 100%;
  text-align: left;
  display: flex;

  .ag-cell div {
    width: fit-content !important;
  }
`;

export const ReportsCell = styled.span<{ completed: boolean }>`
  max-height: 16px;
  padding: 4px 8px;
  border-radius: 6px;
  background-color: ${({ completed, theme }) =>
    completed ? theme.rowNotReadGreen : undefined} !important;
  border: 1px solid
    ${({ completed, theme }) => (completed ? "#017e72" : theme.neutral100)};
  color: ${({ completed, theme }) => (completed ? "#017e72" : theme.greyDark)};
  margin: auto;
  cursor: pointer;
  align-items: center;
  display: flex;
  align-items: baseline;
  line-height: normal;
`;

export const InactiveText = styled.div`
  color: ${({ theme }) => theme.greyMedium};
  text-align: left;
  user-select: all;
`;

export const NoRowsRendererGrid = styled.div`
  display: grid;
  grid-row-gap: 10px;
  grid-template-rows: auto auto;
`;

export const NoRowsRendererSearch = styled.div`
  font-style: normal;
  font-weight: 400;
  font-size: 20px;
  line-height: 28px;

  color: ${({ theme }) => theme.black} !important;
`;

export const NoRowsRendererMessage = styled.div`
  font-weight: 400;
  font-size: 18px;
  line-height: 28px;
  color: ${({ theme }) => theme.greyMedium} !important;
`;

export const TextWrap = styled(FormLabel)`
  color: ${({ theme }) => theme.redMedium} !important;
  font-size: 16px;
  font-weight: bold !important;
  margin-left: 7px;
  font-family: light;
`;

// <!----------------------------headers------------------------------>!

const getHeaderColumns = (
  deviceType: DeviceType,
  globalSearchEnabled?: boolean,
): string => {
  if (deviceType !== "desktop") return "1fr";
  if (globalSearchEnabled)
    return "clamp(240px, 28vw, 320px) 80px minmax(0, 1fr) auto";
  return "clamp(240px, 28vw, 320px) minmax(0, 1fr) auto";
};

const getHeaderAreas = (
  deviceType: DeviceType,
  globalSearchEnabled?: boolean,
): string => {
  if (deviceType !== "desktop") return '"search" "actions" "tabs"';
  if (globalSearchEnabled) return '"search global-search tabs actions"';
  return '"search tabs actions"';
};

export const HeaderLayout = styled.div<{
  deviceType: DeviceType;
  globalSearchEnabled?: boolean;
}>`
  display: grid;
  grid-template-columns: ${({ deviceType, globalSearchEnabled }) =>
    getHeaderColumns(deviceType, globalSearchEnabled)};
  grid-template-areas: ${({ deviceType, globalSearchEnabled }) =>
    getHeaderAreas(deviceType, globalSearchEnabled)};
  gap: ${({ deviceType }) => (deviceType === "desktop" ? "40px" : "12px")};
  width: 100%;
  min-width: 0;
`;

export const HeaderSearchArea = styled.div`
  grid-area: search;
  min-width: 0;
`;

export const GlobalSearchButtonArea = styled.div`
  grid-area: global-search;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 -12px;
`;

export const GlobalSearchIconButton = styled.button`
  border: 0;
  width: 80px;
  height: 40px;
  border-radius: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ theme }) => theme.redMedium};
  color: ${({ theme }) => theme.white};
  cursor: pointer;
  font-size: 20px;

  &:hover {
    background: ${({ theme }) => theme.redDark};
  }
`;

export const HeaderTabsArea = styled.div`
  grid-area: tabs;
  min-width: 0;
  display: flex;
  align-items: center;
`;

export const HeaderActionsArea = styled.div`
  grid-area: actions;
  min-width: 0;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
  flex-wrap: wrap;

  @media (max-width: 850px) {
    justify-content: flex-end;
  }
`;

export const SearchInputBox = styled.div`
  display: block;
  width: 100%;
  position: relative;
  .MuiInputBase-root {
    border: 1px solid ${({ theme }) => theme.greyLight};
    border-radius: 30px;
    width: 100%;
    background-color: ${({ theme }) => theme.white};
    padding-inline: 8px;
  }
  .MuiInputBase-input {
    padding: 8px 20px 8px 8px;
  }
  .MuiInputBase-input::placeholder,
  .MuiInputAdornment-root .MuiSvgIcon-root {
    color: ${({ theme }) => theme.greyLight};
    opacity: 1;
  }
  .MuiInputBase-input::placeholder {
    font-weight: bold;
  }
`;

export const SubGridHeaderTabs = styled.div`
  display: flex;
  height: fit-content;
  align-items: end;
  align-self: center;
  margin-top: 3px;
  @media (max-width: 850px) {
    align-self: flex-start;
  }
`;

export const SubGridHeaderTabButtonWrapper = styled.div`
  display: flex;
  border-radius: 50px;
  min-height: 40px;
  width: 100%;
  min-width: 0;
  flex-wrap: wrap;
  gap: 4px;

  button {
    padding: 4px 24px !important;
  }
`;

export const HeaderTabsContainer = styled.div`
  display: flex;
  height: fit-content;
  align-items: center;
  min-width: 0;
  @media (max-width: 850px) {
    width: 100%;
  }
`;

export const HeaderActionItem = styled.div`
  display: flex;
  width: fit-content;
  align-items: center;
`;

export const HeaderActionNeededItem = styled.div<{
  deviceType: DeviceType;
}>`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  width: fit-content;
  min-height: 40px;
  @media (max-width: 800px) {
    width: 50px;
  }
`;

export const SubTabNumber = styled.div<{
  selected: boolean;
  reqNumber: number;
}>`
  width: ${({ reqNumber }) => (reqNumber < 99 ? "24px" : "32px")};
  height: ${({ reqNumber }) => (reqNumber < 99 ? "24px" : "32px")};
  flex-shrink: 0;
  border-radius: 20px;
  background: ${({ selected, theme }) =>
    selected ? theme.redMedium : "#E7EBF2"};
  color: ${({ selected, theme }) => (selected ? "white" : theme.greyMedium)};
  margin-left: 5px;
  font-size: 14px;
  font-style: normal;
  font-weight: 800;
  line-height: 20px;
  display: flex;
  justify-content: center;
  align-items: center;
`;

export const SubTabCount = styled.span`
  margin: 1px 1px 0 0;
`;

export const SubCustomGridTab = styled(Typography).attrs({
  component: "div",
})<{ selected: boolean }>`
  padding: 8px 20px;
  background-color: transparent;
  color: ${({ selected, theme }) =>
    selected ? theme.redMedium : theme.greyMedium};
  cursor: pointer;
  border-bottom: ${({ theme, selected }) =>
    selected && `3px solid ${theme.redMedium}`};
  font-size: 18px;
  font-family: ${({ selected }) =>
    selected ? "bold !important" : "light !important"};
  display: flex;
  align-items: center;
  font-weight: 400;
  line-height: 18px;
  text-align: center;
  letter-spacing: 0.01em;
  font-variant: small-caps;

  @media (max-width: 450px) {
    font-size: 15px !important;
    padding: 8px 12px !important;
  }
`;

export const HeaderTabsScroller = styled.div`
  display: flex;
  background-color: ${({ theme }) => theme.neutral50};
  border: 1px solid ${({ theme }) => theme.neutral100};
  border-radius: 50px;
  min-height: 40px;
  min-width: 0;
  max-width: 100%;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;

  button {
    flex: 0 0 auto;
    padding: 4px 24px !important;
  }

  &::-webkit-scrollbar {
    display: none;
  }

  @media (max-width: 850px) {
    width: 100%;
    border-radius: 12px;
  }
`;

export const CustomGridTab = styled(Typography).attrs({
  component: "div",
})<{ selected: boolean }>`
  padding: 8px 20px;
  background-color: ${({ selected, theme }) =>
    selected ? theme.neutral100 : theme.neutral50};
  color: ${({ selected, theme }) =>
    selected ? theme.greyDark : theme.greyMedium};
  border-radius: 50px;
  cursor: pointer;
  font-size: 18px;
  font-family: ${({ selected }) =>
    selected ? "bold !important" : "light !important"};
  display: flex;
  align-items: center;
  font-weight: 400;
  line-height: 18px;
  text-align: center;
  letter-spacing: 0.01em;
  font-variant: small-caps;

  @media (max-width: 450px) {
    font-size: 15px !important;
    padding: 8px 12px !important;
  }
`;

export const MissingInfoWarning = styled.div<{ $missingInfo?: boolean }>`
  box-sizing: border-box;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  border-radius: 30px;
  width: 240px;
  max-width: 240px;
  background-color: ${({ $missingInfo, theme }) =>
    $missingInfo ? theme.redMedium : theme.black};
  border: 1px solid
    ${({ $missingInfo, theme }) =>
      $missingInfo ? theme.redMedium : theme.black};
  cursor: pointer;

  :hover {
    opacity: 0.7;
  }
  @media (max-width: 850px) {
    height: 40px;
    width: 50px;
  }
`;

export const MissingInfoWarningText = styled(Typography)<{
  $missingInfo?: boolean;
}>`
  padding: 8px 15px;
  color: ${({ $missingInfo, theme }) =>
    $missingInfo ? theme.white : theme.black};
  border-radius: 50px;
  cursor: pointer;
  font-size: 18px;
  font-family: bold !important;
  display: flex;
  align-items: center;

  @media (max-width: 420px) {
    display: none;
  }
`;

const getDeviceWidth = (deviceType: DeviceType): string => {
  if (deviceType === "mobile") return "360px";
  if (deviceType === "tablet") return "520px";
  return "672px";
};

export const MissingInfoModalWrapper = styled.div<{ deviceType: DeviceType }>`
  display: grid;
  grid-template-rows: 72px 72px 320px auto;
  width: ${({ deviceType }) => getDeviceWidth(deviceType)};
  justify-content: center;
  background: white;
  border-radius: 8px;
  box-shadow: 0 3px 7px rgba(0, 0, 0, 0.3);
`;
export const PasswordModalWrapper = styled(MissingInfoModalWrapper)<{
  deviceType: DeviceType;
}>`
  grid-template-rows: 72px auto auto;
  width: auto;
  @media (max-width: 420px) {
    width: 95%;
  }
`;

export const DraftInfoModalWrapper = styled.div<{ deviceType: DeviceType }>`
  display: grid;
  grid-template-rows: 72px 72px auto;
  width: ${({ deviceType }) => getDeviceWidth(deviceType)};
  justify-content: center;
  background: white;
  border-radius: 8px;
  box-shadow: 0 3px 7px rgba(0, 0, 0, 0.3);

  ${SpinnerWrapper} {
    margin: 0px;
    margin-left: 10px;
    img {
      height: 35px;
      width: 35px;
    }
  }
`;

export const MissingInfoModalTitle = styled.div<{ deviceType: DeviceType }>`
  display: flex;
  height: 72px;
  align-items: center;
  background: #e7ebf2;
  width: ${({ deviceType }) => getDeviceWidth(deviceType)};
  box-sizing: border-box;
  padding-left: 25px;
  font-weight: 700;
  font-size: 16px;
  line-height: 24px;
  letter-spacing: 1.2px;
  text-transform: uppercase;
  border-radius: 8px;
  border-bottom-right-radius: unset;
  border-bottom-left-radius: unset;
  svg {
    margin-top: -4px;
    margin-left: 5px;
    cursor: pointer;
  }
`;
export const PasswordModalTitle = styled(MissingInfoModalTitle)<{
  deviceType: DeviceType;
}>`
  width: auto;
`;
export const AddOnModalTitle = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  box-sizing: border-box;
  padding: 16px;
  background: ${({ theme }) => theme.neutral50};
  color: ${({ theme }) => theme.neutral700};
  font-family: bold;
  font-weight: 700;
  font-size: 18px;
  line-height: 24px;
  text-transform: uppercase;
  border-radius: 8px;
  border-bottom-right-radius: unset;
  border-bottom-left-radius: unset;
  svg {
    cursor: pointer;
  }
`;
export const DraftInfoModalMessage = styled.div`
  color: var(--Blurb-text, #444040);
  font-size: 18px;
  font-style: normal;
  font-weight: 400;
  line-height: 24px;
  display: flex;
  align-items: center;
  padding-right: 25px;
  margin-top: 20px;
  margin-left: 20px;
  display: inline;
  span {
    font-weight: 600;
    font-size: 16px;
  }
`;

export const DraftInfoModalPatientMessage = styled.div`
  display: flex;
  padding: 8px 16px;
  align-items: center;
  gap: 32px;
  align-self: stretch;
  background: var(--Neutral-50, #f9f9f9);
`;

export const DraftInfoModalMessageGrid = styled.div`
  display: grid;
  grid-template-columns: auto auto;
  padding-left: 25px;
  gap: 10px;
  svg {
    margin: 0px 0px !important;
  }

  ${DraftInfoModalMessage} {
    padding-left: 0px;
  }
`;

export const MissingInfoModalMessage = styled.div`
  display: flex;
  align-items: center;
  padding-left: 25px;
  font-weight: 400;
  font-size: 18px;
  line-height: 28px;
`;

export const MissingInfoModalGridWrapper = styled.div`
  margin: 0px 20px;
`;

export const MissingInfoModalGrid = styled.div`
  display: grid;
  height: 100%;
  grid-auto-rows: auto;
  overflow: scroll;
  border: 1px solid #dbdee6;
  border-radius: 6px;

  ::-webkit-scrollbar {
    -webkit-appearance: none;
    width: 8px;
  }

  ::-webkit-scrollbar-thumb {
    border-radius: 4px;
    background-color: rgba(0, 0, 0, 0.5);
    box-shadow: 0 0 1px rgba(255, 255, 255, 0.5);
  }
`;

export const MissingInfoModalGridRowWrapper = styled.div<{ header?: boolean }>`
  display: grid;
  align-items: center;
  grid-template-columns: 40% 60%;
  font-weight: 800;
  border-top: ${({ header }) => (!header ? "1px solid #dbdee6" : "none")};
  padding-left: 10px;
  cursor: pointer;

  :hover {
    background-color: ${({ theme }) => theme.rowMissingInfoRed};
  }
`;

export const MissingInfoModalGridRow = styled.div`
  display: flex;
  align-items: center;
  text-transform: uppercase;
  color: ${({ theme }) => theme.redDark};
  font-weight: 800;
  cursor: pointer;
  padding-left: 10px;
  font-size: 14px;
`;

export const MissingInfoStatusIndicator = styled.div`
  width: auto;
  height: 28px;
  left: 12px;
  top: 19px;
  font-weight: 400;
  font-size: 14px;
  align-items: center;
  display: inline-flex;
  padding: 2px 10px;
  margin-top: 5px;
  margin-right: 5px;
  margin-bottom: 5px;
  flex-wrap: wrap;
  justify-content: center;
  line-height: 20px;
  color: ${({ theme }) => theme.neutral700};
  background: ${({ theme }) => theme.neutral50};
  border: 1px solid ${({ theme }) => theme.neutral100};
  border-radius: 6px;
`;

export const DraftModalButtons = styled.div`
  display: flex;
  grid-gap: 8px;
  padding: 20px;
  justify-content: flex-end;

  button {
    color: #433f3f !important;
    padding: 5px 20px !important;
    border: 1px solid var(--Neutral-500, #929499) !important;
  }
`;

export const ModalButtons = styled.div`
  display: flex;
  grid-gap: 8px;
  padding: 20px;
  justify-content: flex-end;

  button {
    background-color: #e7ebf2 !important;
    color: #433f3f !important;
    padding: 5px 20px !important;
  }
`;

export const AddOnModalButtons = styled.div`
  display: flex;
  grid-gap: 8px;
  padding: 15px 20px;
  justify-content: flex-end;
  height: 44px;
  gap: 15px;

  button {
    background-color: #e7ebf2 !important;
    color: #433f3f !important;
    padding: 5px 20px !important;
    width: auto;
  }

  .add-on {
    background-color: ${({ theme }) => theme.redMedium} !important;
    color: white !important;
  }
`;

export const PatientReleaseModalGridWrapper = styled.div`
  margin: 0px 20px;
`;

export const PatientReleaseModalKey = styled.div`
  border: 1px dashed ${({ theme }) => theme.neutral300};
  border-radius: 8px;
  padding: 4px 4px;
  display: grid;
  grid-template-rows: auto;
  grid-row-gap: 15px;
  margin: 0px 25px 20px 20px;
  justify-self: flex-start;
  padding: 10px 10px;
`;

export const PatientReleaseModalKeyRow = styled.div`
  display: grid;
  grid-template-columns: 190px auto;
  grid-column-gap: 10px;
`;

export const PatientReleaseModalKeyTitle = styled.div`
  color: ${({ theme }) => theme.black};
  font-size: 14px;
  font-weight: 700;
  justify-self: flex-start;
  align-self: center;
`;

export const PatientReleaseModalKeyDescription = styled.div`
  color: ${({ theme }) => theme.greyDark};
  font-size: 14px;
  justify-self: flex-start;
  align-self: center;
`;

export const PatientReleaseReleaseIndicator = styled.div<{
  button?: boolean;
  modal?: boolean;
}>`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  padding: 1px 0px;
  gap: 8px;
  width: ${({ modal }) => (modal ? "157px" : "120px")} !important;
  height: 32px;
  left: 12px;
  top: 17px;
  border-radius: 30px;
  color: ${({ theme }) => theme.redMedium};
  background: ${({ theme }) => theme.white};
  border: 1px solid ${({ theme }) => theme.redMedium};
  cursor: ${({ button }) => (button ? "pointer" : undefined)};
  svg {
    margin-left: 3px;
    width: 20px;
    margin-top: -2px;
  }
  :hover {
    background: ${({ theme }) => theme.redPastel};
  }
`;

export const PatienReleaseIndicator = styled.span<{
  button?: boolean;
  modal?: boolean;
}>`
  display: flex;
  flex-direction: row;
  font-size: 14px;
  font-style: normal;
  font-weight: 800 !important;
  height: 36px;
  width: 85px;
  display: inline-flex;
  flex: none;
  padding: 0px 12px;
  margin-left: 15px;
  justify-content: center;
  align-items: center;
  border-radius: 30px;
  color: ${({ theme }) => theme.redMedium};
  border: 1px solid ${({ theme }) => theme.redMedium};
  background: ${({ theme }) => theme.white};
  cursor: ${({ button }) => (button ? "pointer" : undefined)};
  margin-right: 5px;
  z-index: 5;
  :hover {
    background: ${({ theme }) => theme.redPastel};
  }
`;

export const PatientReleaseModalWrapper = styled.div<{
  deviceType: DeviceType;
}>`
  display: grid;
  grid-template-rows: 72px 200px auto;
  width: ${({ deviceType }) => getDeviceWidth(deviceType)};
  justify-content: center;
  background: white;
  border-radius: 8px;
  box-shadow: 0 3px 7px rgba(0, 0, 0, 0.3);
  max-height: ${({ deviceType }) =>
    deviceType === "mobile" ? "80%" : undefined};
  overflow: ${({ deviceType }) =>
    deviceType === "mobile" ? "scroll" : undefined};
`;

export const FetalAddOnModalWrapper = styled.div<{
  deviceType: DeviceType;
}>`
  display: grid;
  grid-template-rows: 72px auto auto;
  width: ${({ deviceType }) => getDeviceWidth(deviceType)};
  justify-content: center;
  background: white;
  border-radius: 8px;
  box-shadow: 0 3px 7px rgba(0, 0, 0, 0.3);
  max-height: ${({ deviceType }) =>
    deviceType === "mobile" ? "80%" : undefined};
  overflow: ${({ deviceType }) =>
    deviceType === "mobile" ? "scroll" : undefined};
  svg {
    margin-top: -4px;
    margin-right: 5px !important;
  }

  span {
    font-weight: bold;
  }
`;

export const AddOnModalWrapper = styled(FetalAddOnModalWrapper)<{
  deviceType: DeviceType;
}>`
  && {
    grid-template-columns: 1fr;
    grid-template-rows: 72px minmax(0, 1fr) auto auto;
    width: ${({ deviceType }) =>
      deviceType === "desktop" ? "750px" : getDeviceWidth(deviceType)};
    max-height: calc(100vh - 32px);
    overflow: hidden;

    span {
      font-weight: 400;
    }
  }
`;

export const PatientReleaseModalGrid = styled.div`
  margin-top: 20px;
  display: grid;
  grid-template-rows: 45px 55px 55px;
  border: 1px solid #dbdee6;
  border-radius: 8px;
`;

export const PatientReleaseTooltipRow = styled.div`
  margin-top: 4px;
  font-size: 14px;
`;

export const PatientReleaseModalGridRow = styled.div<{ secondaryRow: boolean }>`
  display: grid;
  align-items: center;
  grid-template-columns: 50% 50%;
  color: ${({ theme }) => theme.redMedium};
  font-weight: 800;
  border-top: ${({ secondaryRow }) =>
    secondaryRow ? "1px solid #dbdee6" : undefined};
  padding-left: 10px;
`;

export const PatientReleaseModalText = styled.div<{
  heading: boolean;
  bold?: boolean;
}>`
  display: flex;
  align-items: center;
  text-transform: ${({ heading }) => (heading ? "uppercase" : undefined)};
  color: ${({ theme, heading }) => (heading ? theme.neutral400 : theme.black)};
  font-weight: ${({ bold }) => (bold ? "800" : "400")};
  font-size: ${({ heading }) => (heading ? "14px" : "16px")};
  height: 40px;
`;

export const PatientReleaseStatusIndicator = styled.div<{ button?: boolean }>`
  width: fit-content !important;
  padding: 0px 10px;
  height: 28px;
  left: 12px;
  top: 19px;
  font-weight: 400 !important;
  font-family: light;
  font-size: 14px;
  align-items: center;
  display: flex;
  justify-content: center;
  line-height: 20px;
  color: ${({ theme }) => theme.neutral700};
  background: ${({ theme }) => theme.neutral50};
  border: 1px solid ${({ theme }) => theme.neutral100};
  border-radius: 6px;
  cursor: ${({ button }) => (button ? "pointer" : undefined)};
  svg {
    margin-left: 3px;
    width: 20px;
    margin-top: -2px;
  }
`;

// <!----------------------------rhd header cell------------------------------>!

export const RhdHeaderCellWrapper = styled.div`
  font-size: 14px;
  color: #5d5a5a;
  text-align: left;
  display: flex;
  align-items: center;

  svg {
    width: 18px;
    height: 18px;
    margin-left: 4px;
    cursor: pointer;
  }
`;

export const StyledPhlebotomySnackbar = styled(Snackbar)`
  .MuiAlert-root {
    background-color: ${({ theme }) => theme.greyDark};
    color: white;
    font-family: light;
    line-height: 1.5;
    padding-top: 13px;
  }
`;

export const FetalAntigenFieldsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;

  ${OptionLabel} {
    color: ${({ theme }) => theme.primaryTextColor};
  }

  ${CheckboxLabelWrapper} {
    margin-left: 0;
    gap: 5px;

    .MuiCheckbox-root {
      margin: 0;
      padding: 0;
    }
  }

  ${NewReqFetalAntigenGrid},
  ${CollapsiblePlateletGrid} {
    row-gap: 6px;

    @media (max-width: 1300px) {
      grid-template-columns: repeat(3, minmax(0, 1fr));
    }

    @media (max-width: 600px) {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }
  }

  ${CollapsiblePlateletGrid} {
    padding-top: 10px;
  }

  ${FetalAntigenIndentUploadGrid} {
    margin-top: 5px;

    .dzu-dropzone {
      background: transparent !important;
    }

    ${NewReqFileUploaderDropzoneAntigen} {
      box-sizing: border-box;
      background: ${({ theme }) => theme.neutral50};
    }
  }
`;
